import type { VoteEntity } from "@/appInterface/windows/VoteWindow/VoteResults/types";
import { canv } from "@/canv/global";
import { logException, sleep } from "@/canv/utils";
import type { elementBase } from "@/elementBase/elementBase";
import { AiAnswer } from "@/elements/aiAnswer";
import { arrowWidget } from "@/elements/arrow";
import { cardWidget } from "@/elements/card/card";
import { CommentWidget } from "@/elements/comment";
import { DocumentViewerWidget } from "@/elements/documentViewer";
import { EmbedWidget } from "@/elements/embed";
import { EmojiWidget } from "@/elements/emoji";
import { frameWidget } from "@/elements/frame";
import { frameOverlappedManager } from "@/elements/frame/FrameOverlappedManager";
import { GifWidget } from "@/elements/gif";
import { ImageWidget } from "@/elements/image";
import { mindMapWidget } from "@/elements/mindMap/mindMap";
import { PresentationWidget } from "@/elements/Presentation/Presentation";
import { ShapeWidget } from "@/elements/shape";
import { SketchWidget } from "@/elements/sketch";
import { stickerWidget } from "@/elements/sticker";
import { TableWidget } from "@/elements/table/table";
import { textWidget } from "@/elements/text/text";
import { TextWidgetV2 } from "@/elements/text/textV2";
import { TreeNodeWidget } from "@/elements/treeNode";
import { eventEmitter } from "@/eventEmitter";
import { Group } from "@/group";
import { HashGrid } from "@/hashGrid";
import { generateKeyBetween, generateNKeysBetween } from "@/helpers/fractional-indexing.js";
import { limiter } from "@/helpers/limiter";
import type { ProgressBar } from "@/progressBar/progressBar";
import timerController from "@/timerController/timerController";
import type { TimerEntity } from "@/timerController/types";
import { selectionManager } from "@/tools/cursorTool";
import { queryAreaByGroup } from "@/utils";
import { copyProccessorExec, loadGroupsTree, pasteGroups, sortByIndexByParent } from "./helpers";
import type { Board, BoardEntity, Entity, ExtendsOfElementBase } from "./types";

const UPLOAD_PARALLEL_IMAGES_LIMIT = 10;

export const uploadImageLimit = limiter(UPLOAD_PARALLEL_IMAGES_LIMIT);

export class canvasController {
    async loadCanv(canvStr: string) {
        this.items = [];

        let groups: Group[];

        try {
            groups = loadGroupsTree(canvStr);
        } catch (e) {
            throw 1;
        }

        const needInitWidgets: elementBase[] = [];

        for (let i = 0; i < groups.length; i++) {
            this.items.push(groups[i]);
            groups[i].collectNeedInitWidgets(needInitWidgets);
        }

        for (let i = 0; i < needInitWidgets.length; i++) {
            needInitWidgets[i].initWidget();
        }

        sortByIndexByParent(this.items);
    }

    // не чистит доску, а просто добавляет новые entities
    async loadFlatEnitites(boardStr: string, needToChangeUID = false) {
        let board: Board = { entities: [] };

        try {
            board = JSON.parse(boardStr);
        } catch (e) {
            logException(new Error("Board parsing fail 2"));
            throw 1;
        }

        const entities = new Map<string, Entity>();
        const progressBar = canv.progressBar as unknown as ProgressBar;
        let loadState = progressBar.state as number;

        progressBar.moveTo(loadState);

        const loadStep = (100 - loadState) / board.entities.length;
        const batchSize = 350;

        // наполняем мапу реильными enitity
        for (let i = 0; i < board.entities.length; i++) {
            if (i % batchSize === 0) {
                await sleep(100);
                loadState += batchSize * loadStep;
                progressBar.moveTo(loadState);
            }

            const entityType = board.entities[i].entityType;

            if (entityType === "vote") {
                canv.canvasController.addVote(board.entities[i] as VoteEntity);
                continue;
            } else if (entityType === "timer") {
                const timer = board.entities[i] as TimerEntity;
                if (!this.timer) {
                    this.timer = timer;
                    timerController.setTimer(timer);
                } else {
                    // на случай если каким то образом на доске оказалось больше одного таймера
                    timerController.deleteTimer(timer);
                }
                continue;
            } else if (
                entityType === "presentationWidget" &&
                (board.entities[i] as PresentationWidget).isActive
            ) {
                eventEmitter.dispatch("presentation.mode.presentationUID", {
                    UID: board.entities[i].UID,
                });
            }

            const entity = canv.canvasController.getEntityByType(entityType);

            if (entity) {
                // @ts-expect-error - union type of loadState methods is not callable
                entity.loadState(board.entities[i], "full");
                entities.set(entity.UID, entity);
            } else {
                throw new Error("Ошибка: неизвестное entity");
            }
        }
        progressBar.moveTo(100);

        // провязываем entity
        // берем все элементы у которых есть родители и привязываем их
        for (const [UID, entity] of entities) {
            if (entity.parentUID !== "") {
                const element = entity as ExtendsOfElementBase;
                // важно не подвязывать ветки в items, чтобы setParent->updateDims->FindByUID не натыкался на children==[] (недоделанные ветки)
                const parentEntity = entities.get(element.parentUID);

                if (parentEntity) {
                    element.setParent(parentEntity as Group);
                    if (element instanceof Group) {
                        element.addToCollection();
                    }
                }
            }
        }

        const heads: Entity[] = [];
        const needInitWidgets: elementBase[] = [];

        // берем все элементы у которых нет родителя и добавляем их коллекцию
        for (const [UID, entity] of entities) {
            if (entity.parentUID === "") {
                const group = entity as Group;

                if (needToChangeUID) {
                    group.regenerateUIDS("");
                }

                heads.push(group as Group);
                group.setParent(null);
                group.addToCollection();
                group.collectNeedInitWidgets(needInitWidgets);
            }
        }

        for (let i = 0; i < needInitWidgets.length; i++) {
            needInitWidgets[i].initWidget();
        }
        this.hashGrid = new HashGrid();

        return heads;
    }
    addVote(vote: VoteEntity) {
        if (vote.isResolved) {
            canv.canvasController.pastVotes.push(vote);
        } else {
            canv.canvasController.activeVote = vote;
            eventEmitter.dispatch("vote.timer.visibility", {
                isVisible: true,
            });
        }
    }

    deleteActiveVote() {
        if (canv.canvasController.activeVote) {
            canv.canvasController.pastVotes.push(canv.canvasController.activeVote);
            canv.canvasController.activeVote = null;
        }
    }

    deletePastVote(UID: string) {
        canv.canvasController.pastVotes = canv.canvasController.pastVotes.filter(
            (vote) => vote.UID !== UID,
        );
    }

    renamePastVote(UID: string, text: string) {
        canv.canvasController.pastVotes.forEach((vote) => {
            if (vote.UID === UID) {
                vote.text = text;
            }
        });
    }

    getCanv() {
        const states = [];

        for (let i = 0; i < this.items.length; i++) {
            this.items[i].collectEntitiesStates(states);
        }

        const result = {
            entities: states,
        };

        return JSON.stringify(result);
    }

    addGroup(newGroup: Group) {
        let left: string | null = null;

        if (this.items.length !== 0) {
            left = this.items[this.items.length - 1].indexByParent;
        }

        newGroup.indexByParent = generateKeyBetween(left, null);
        newGroup.isGhost = false;

        if (newGroup.isItemWrapper()) {
            this.items.push(newGroup);
        } else {
            this.groups.set(newGroup.UID, newGroup);

            for (const group of newGroup.children) {
                canv.canvasController.addGroup(group as Group);
            }
        }
    }

    updateDims() {
        if (this.selected.length === 0) return;

        let left = this.selected[0].startX;
        let right = this.selected[0].startX + this.selected[0].width;
        let top = this.selected[0].startY;
        let bottom = this.selected[0].startY + this.selected[0].height;

        for (let i = 0; i < this.selected.length; i++) {
            if (this.selected[i].startX < left) {
                left = this.selected[i].startX;
            }

            if (this.selected[i].startX + this.selected[i].width > right) {
                right = this.selected[i].startX + this.selected[i].width;
            }

            if (this.selected[i].startY < top) {
                top = this.selected[i].startY;
            }

            if (this.selected[i].startY + this.selected[i].height > bottom) {
                bottom = this.selected[i].startY + this.selected[i].height;
            }
        }

        this.startX = left;
        this.startY = top;
        this.width = right - left;
        this.height = bottom - top;
    }

    FindByUID(UID: string) {
        // группы теперь лежат в другом месте нам нужно сначала проверить их т.к. это быстрее
        if (this.groups.has(UID)) {
            return this.groups.get(UID);
        }

        for (let k = 0; k < this.items.length; k++) {
            const foundItem = this.items[k].FindByUID(UID);

            if (foundItem) {
                return foundItem;
            }
        }

        return null;
    }

    FindUIDByTrailUID(trailUID: string) {
        let item = "";

        for (let k = 0; k < this.items.length; k++) {
            const foundUID = this.items[k].FindUIDByTrailUID(trailUID);

            if (foundUID !== "") {
                item = foundUID;
                return item;
            }
        }

        return item;
    }

    createBranch(states: BoardEntity[]) {
        const entities = new Map();

        // наполняем мапу реильными enitity
        for (let i = 0; i < states.length; i++) {
            const entityType = states[i].entityType;
            const entity = canv.canvasController.getEntityByType(entityType);

            if (entity) {
                // @ts-expect-error - union type of loadState methods is not callable
                entity.loadState(states[i], "full");
                entities.set(entity.UID, entity);
            }
        }

        const headGroups: Group[] = [];

        for (const [UID, entity] of entities) {
            if (!entities.has(entity.parentUID)) {
                headGroups.push(entity);
            } else {
                const parentEntity = entities.get(entity.parentUID);
                entity.setParent(parentEntity, true); // true при вызове из build event ок, потому что check проверил, что таких entity нету
            }
        }

        return headGroups;
    }

    searchText(text: string): {
        group: Group;
        textWidget: textWidget;
        mode: string;
    }[] {
        if (text.length === 0) return [];

        const foundItems = [];

        for (let i = 0; i < this.items.length; i++) {
            this.items[i].searchText(text, foundItems);
        }

        return foundItems;
    }

    async cutProccessor() {
        if (this.selected.length === 0) return;

        // Собираем все группы для удаления (включая содержимое фреймов)
        const groupsToDelete = frameOverlappedManager.collectGroupsForDeletion(this.selected);

        await this.copyProccessor();

        canv.eventManager.openRecord();

        for (let i = 0; i < groupsToDelete.length; i++) {
            this.deleteProcessor(groupsToDelete[i]);
        }

        canv.eventManager.closeRecord();
        selectionManager.clearSelected();
    }

    async copyProccessor() {
        if (this.selected.length === 0 || this.selected[0]?.children[0] instanceof CommentWidget)
            return;

        const selectedSet = new Set<Group>();
        const visited = new Set<Group>();

        const processGroup = (group: Group) => {
            if (!group || visited.has(group)) return;

            visited.add(group);
            selectedSet.add(group);

            for (const child of group.children) {
                if (child instanceof PresentationWidget) {
                    for (const slide of child.slides) {
                        const frameGroup = canv.canvasController.FindByUID(slide.frameUID);

                        if (frameGroup && frameGroup instanceof Group) {
                            processGroup(frameGroup);

                            if (frameGroup.children[0] instanceof frameWidget) {
                                for (const gr of frameGroup.children[0].getOverlappedGroups()) {
                                    processGroup(gr);
                                }
                            }
                        }
                    }
                }

                if (child instanceof frameWidget) {
                    for (const gr of child.getOverlappedGroups()) {
                        processGroup(gr);
                    }
                }
            }
        };

        for (const group of this.selected) {
            processGroup(group);
        }

        const selectedGroups = Array.from(selectedSet);

        const copiedBranches = selectedGroups.map((group) => {
            const states = [];
            group.collectEntitiesStates(states);
            return states;
        });

        await copyProccessorExec(copiedBranches);
    }

    async duplicateProccessor() {
        if (this.selected.length === 0 || this.selected[0]?.children[0] instanceof CommentWidget) {
            return;
        }

        const selectedProps = this.getSelectedProps(this.selected);

        const groupsSet = new Set<Group>();
        const visited = new Set<Group>();

        const processGroup = (group: Group) => {
            if (!group || visited.has(group)) return;

            visited.add(group);
            groupsSet.add(group);

            for (const child of group.children) {
                if (child instanceof PresentationWidget) {
                    for (const slide of child.slides) {
                        const frameGroup = canv.canvasController.FindByUID(slide.frameUID);

                        if (frameGroup && frameGroup instanceof Group) {
                            processGroup(frameGroup);

                            if (frameGroup.children[0] instanceof frameWidget) {
                                for (const gr of frameGroup.children[0].getOverlappedGroups()) {
                                    processGroup(gr);
                                }
                            }
                        }
                    }
                }

                if (child instanceof frameWidget) {
                    for (const gr of child.getOverlappedGroups()) {
                        processGroup(gr);
                    }
                }
            }
        };

        for (const group of canv.canvasController.selected) {
            processGroup(group);
        }

        const groups = Array.from(groupsSet);

        const copiedBranches: BoardEntity[][] = [];
        for (const group of groups) {
            const states: BoardEntity[] = [];
            group.collectEntitiesStates(states);
            copiedBranches.push(states);
        }

        pasteGroups(
            copiedBranches,
            {
                sourceBoardUid: canv.getInitDataProp("boardUID"),
                sourceBoardAccessToken: canv.getInitDataProp("boardAccessToken"),
            },
            {
                x: selectedProps.startX + canv.actionManager.getConstant(50),
                y: selectedProps.startY + canv.actionManager.getConstant(50),
            },
        );
    }

    alignSelected(type: string) {
        canv.eventManager.openRecord();

        // Очищаем менеджер overlapped групп перед началом
        frameOverlappedManager.clearOverlapped();

        for (let i = 0; i < this.selected.length; i++) {
            this.selected[i].groupTranslatingStart();
        }
        for (let i = 0; i < this.selected.length; i++) {
            this.selected[i].fixWidgets("move");
        }

        if (type === "left") {
            for (let i = 0; i < this.selected.length; i++) {
                this.selected[i].translateGroup(this.startX - this.selected[i].startX, 0);
            }
        } else if (type === "hCenters") {
            for (let i = 0; i < this.selected.length; i++) {
                this.selected[i].translateGroup(
                    this.startX +
                        this.width / 2 -
                        (this.selected[i].startX + this.selected[i].width / 2),
                    0,
                );
            }
        } else if (type === "right") {
            for (let i = 0; i < this.selected.length; i++) {
                this.selected[i].translateGroup(
                    this.startX + this.width - (this.selected[i].startX + this.selected[i].width),
                    0,
                );
            }
        } else if (type === "top") {
            for (let i = 0; i < this.selected.length; i++) {
                this.selected[i].translateGroup(0, this.startY - this.selected[i].startY);
            }
        } else if (type === "vCenters") {
            for (let i = 0; i < this.selected.length; i++) {
                this.selected[i].translateGroup(
                    0,
                    this.startY +
                        this.height / 2 -
                        (this.selected[i].startY + this.selected[i].height / 2),
                );
            }
        } else if (type === "bottom") {
            for (let i = 0; i < this.selected.length; i++) {
                this.selected[i].translateGroup(
                    0,
                    this.startY + this.height - (this.selected[i].startY + this.selected[i].height),
                );
            }
        }
        for (let i = 0; i < this.selected.length; i++) {
            this.selected[i].groupTranslatingStop();
        }
        for (let i = 0; i < this.selected.length; i++) {
            this.selected[i].saveWidgets("move");
        }

        // Очищаем менеджер после завершения
        frameOverlappedManager.clearOverlapped();

        canv.eventManager.closeRecord();
    }

    toProccessor(edge: string) {
        canv.eventManager.openRecord();
        this.toProccessorImpl(edge);

        if (edge === "back") {
            this.updateLevelOverFrames();
        }

        canv.eventManager.closeRecord();
    }

    toProccessorImpl(edge: string, afterGroup: Group | null = null) {
        if (this.selected.length === 0) return;

        let container = this.items;

        if (this.selected[0].parent != null) {
            container = this.selected[0].parent.children as Group[];
        }

        const toProcess = this.selected;

        sortByIndexByParent(toProcess);

        let from: string | null = container[container.length - 1].indexByParent;
        let to: string | null = null;

        if (afterGroup != null) {
            // Сюда попадаем только из updateLevelOverFrames
            from = afterGroup.indexByParent;
            for (let i = 0; i < container.length; i++) {
                if (container[i] === afterGroup && i !== container.length - 1) {
                    to = container[i + 1].indexByParent;
                }
            }
        } else if (edge === "back") {
            from = null;
            to = container[0].indexByParent;
        }

        const newIndexes = generateNKeysBetween(from, to, toProcess.length);

        for (let i = 0; i < toProcess.length; i++) {
            toProcess[i].fixState("indexByParent");
            toProcess[i].indexByParent = newIndexes[i];
            toProcess[i].saveState(); // тут для группы можно не передавать context в saveState
        }

        sortByIndexByParent(container);
    }

    updateLevelOverFrames() {
        const area = {
            x: this.startX,
            y: this.startY,
            width: this.width,
            height: this.height,
        };

        let container = canv.canvasController.items;

        if (this.selected[0]?.parent != null) {
            container = this.selected[0].parent.items;
        }

        for (let i = container.length - 1; i >= 0; i--) {
            if (this.isInSelected(container[i])) {
                continue;
            }

            if (
                !container[i].isItemWrapper() ||
                container[i].onlyChild()?.constructorName !== "frame"
            ) {
                continue;
            }

            const over = container[i].isGroupInArea(area);

            if (!over) {
                continue;
            }

            this.toProccessorImpl("front", container[i]);
        }
    }

    getSelected() {
        return this.selected;
    }

    getFirstLayerGroups() {
        const result: Group[] = [];

        for (let i = 0; i < this.items.length; i++) {
            if (this.items[i].parentUID === "" && !this.items[i].isLocked) {
                result.push(this.items[i]);
            }
        }

        this.groups.forEach((g) => {
            if (g.parentUID === "" && !g.isLocked) {
                result.push(g);
            }
        });

        return result;
    }

    getVisibleItemWrappersExceptSelected(): Group[] {
        const visibleWrappers = [];

        for (let i = 0; i < this.items.length; i++) {
            this.items[i].getVisibleItemWrappersExceptSelected(visibleWrappers);
        }

        return visibleWrappers;
    }

    isInSelected(group: Group | elementBase) {
        for (let k = this.selected.length - 1; k >= 0; k--) {
            if (this.selected[k] === group) {
                return true;
            }
        }

        return false;
    }

    isInSelectedDeep(group: Group) {
        for (let k = this.selected.length - 1; k >= 0; k--) {
            if (this.selected[k].hasSubGroup(group)) {
                return true;
            }
        }

        return false;
    }

    isEqualToSelected(groups: Group[]) {
        if (this.selected.length !== groups.length) {
            return false;
        }

        for (let i = 0; i < groups.length; i++) {
            const index = this.selected.indexOf(groups[i]);

            if (index === -1) {
                return false;
            }
        }

        return true;
    }

    /**
     *
     * @param { Group[] | null } specialGroups
     * @returns {{ startX: number, startY: number, width: number, height: number }}
     */
    getSelectedProps(specialGroups: Group[] | null = null) {
        let groups = this.selected;

        if (specialGroups != null) {
            groups = specialGroups;
        }

        const data = {
            startX: 0,
            startY: 0,
            width: 0,
            height: 0,
        };

        if (groups.length === 0) return data;

        let left = groups[0].startX;
        let right = groups[0].startX + groups[0].width;
        let top = groups[0].startY;
        let bottom = groups[0].startY + groups[0].height;

        for (let i = 0; i < groups.length; i++) {
            if (groups[i].startX < left) {
                left = groups[i].startX;
            }

            if (groups[i].startX + groups[i].width > right) {
                right = groups[i].startX + groups[i].width;
            }

            if (groups[i].startY < top) {
                top = groups[i].startY;
            }

            if (groups[i].startY + groups[i].height > bottom) {
                bottom = groups[i].startY + groups[i].height;
            }
        }

        data.startX = left;
        data.startY = top;
        data.width = right - left;
        data.height = bottom - top;

        return data;
    }

    groupProccessor(mode: "group" | "ungroup") {
        if (this.selected.length === 0) return;

        canv.eventManager.openRecord();

        const respectNesting = localStorage.getItem("respectNesting") === "true";

        let result: Group[] = [];
        if (mode === "ungroup") {
            this.selected.forEach((group) => {
                result.push(...this.TryUngroup(group));
            });
        } else {
            result = this.TryGroup(this.selected, respectNesting);
        }

        canv.eventManager.closeRecord();
        selectionManager.setSelected(result as Group[]);
    }

    TryUngroup(group: Group) {
        if (group.isItemWrapper()) return [group];

        if (group.parentUID !== "") {
            canv.eventManager.createTouchEvent(group.parentUID);
        }

        const childGroups = [...group.children] as Group[];

        // тут важно итерироваться по childGroups, а не по group.children, потому что каждая итерация for уменьшает group.children
        for (let i = 0; i < childGroups.length; i++) {
            childGroups[i].fixState("parentUID");
            childGroups[i].setParent(group.parent); // если null это ок
            childGroups[i].saveState();
        }

        // нужно чистить children что бы они не удалились вместе с группой
        group.children = [];

        canv.eventManager.createBuildEvent("delete", group);

        canv.canvasController.groups.delete(group.UID);

        return childGroups;
    }

    processForGrouping(items: Group[]) {
        const toInclude: Group[] = [];
        const toDelete: Group[] = [];

        for (const item of items) {
            if (item.itemWrapper) {
                toInclude.push(item);
            } else if (!item.children || item.children.length === 0) {
                toInclude.push(item);
            } else {
                toDelete.push(item);

                const childResult = this.processForGrouping(item.children as Group[]);

                toInclude.push(...childResult.toInclude);
                toDelete.push(...childResult.toDelete);
            }
        }

        return { toInclude, toDelete };
    }

    deleteGroups(toDelete: Group[]) {
        for (const group of toDelete) {
            if (!group.itemWrapper) {
                group.children = [];
                canv.eventManager.createBuildEvent("delete", group);
            }
        }
    }

    makeTree(uid: string) {
        const target = this.FindByUID(uid);
        if (!target) {
            console.log("not found");
            return;
        }
        const getRootParent = (g: Group | elementBase) => {
            if (!g.parent) {
                return g;
            }
            return getRootParent(g.parent);
        };
        const logGroup = (g: Group | elementBase) => {
            const isTarget = uid === g.UID;
            let postfix = "";
            postfix += "isLocked" in g && g.isLocked ? "🔒" : "";
            postfix += isTarget ? "📌" : "";
            if (!(g instanceof Group)) {
                console.log(`[${g.constructorName}] ${g.UID}`, postfix);
                return;
            }
            if (g.isItemWrapper()) {
                console.group(`[wrapper] ${g.UID}`, postfix);
                g.children.forEach((e) => {
                    logGroup(e);
                });
                console.groupEnd();
            } else {
                console.group(`[group] ${g.UID}`, postfix);
                g.children.forEach((ch) => {
                    logGroup(ch);
                });
                console.groupEnd();
            }
        };
        const rootParent = getRootParent(target);
        logGroup(rootParent);
    }

    TryGroup(selectedItems: Group[], respectNesting = false, indexByParent: string | null = null) {
        let toInclude: Group[] = [];
        let toDelete: Group[] = [];

        if (respectNesting) {
            // включаем только верхнеуровневые элементы из выделения, сохраняя их внутреннюю структуру
            const selectedSet = new Set(selectedItems);
            const isAncestorSelected = (g: Group) => {
                for (const candidate of selectedSet) {
                    if (candidate !== g && candidate.hasSubGroup(g)) return true;
                }
                return false;
            };
            toInclude = selectedItems.filter((g) => !isAncestorSelected(g));
        } else {
            const result = this.processForGrouping(selectedItems);
            toInclude = result.toInclude;
            toDelete = result.toDelete;
        }

        if (toInclude.length === 0) {
            this.deleteGroups(toDelete);
            return [];
        }

        const newGroup = new Group();

        const left =
            this.items.length !== 0 ? this.items[this.items.length - 1].indexByParent : null;

        newGroup.indexByParent = indexByParent || generateKeyBetween(left, null);

        canv.eventManager.createBuildEvent("create", newGroup);

        if (selectedItems[0].parentUID !== "") {
            canv.eventManager.createTouchEvent(selectedItems[0].parentUID);
            newGroup.fixState("parentUID");
            newGroup.setParent(selectedItems[0].parent);
            newGroup.saveState();
        } else {
            newGroup.setParent(null);
        }

        newGroup.addToCollection();

        for (const el of toInclude) {
            el.fixState("parentUID");
            el.setParent(newGroup);
            el.saveState();
        }

        // Удаление всех обработанных групп
        this.deleteGroups(toDelete);

        return [newGroup];
    }

    deleteProcessor(group: Group, ignoreEvent = false, withAnchorGroups = true) {
        const anchorGroups = withAnchorGroups ? queryAreaByGroup(group, false, true) : null;

        group.beforeDeletion();

        if (group.parentUID !== "") {
            canv.eventManager.createTouchEvent(group.parentUID);
        }

        // если это itemWrapper то мы должны его просто удалить
        if (group.isItemWrapper()) {
            // удаляем группу из родителя в любом случае
            if (group.parent) {
                group.parent.children = group.parent.children.filter((g) => g.UID !== group.UID);
            }

            for (let i = 0; i < this.items.length; i++) {
                if (this.items[i] === group) {
                    if (!ignoreEvent) {
                        canv.eventManager.createBuildEvent("delete", this.items[i]);
                    }
                    this.items.splice(i, 1);
                    break;
                }
            }
        } else {
            // родитель будет только isLocked поэтому сразу ее разгруппировываем
            if (group.parent) {
                this.TryUngroup(group.parent);
            }

            group.children.forEach((childGroup) => {
                canv.canvasController.deleteProcessor(
                    childGroup as Group,
                    ignoreEvent,
                    withAnchorGroups,
                );
            });
        }

        if (group.parent != null) {
            group.parent.updateDims();
        }

        if (group.parent != null) {
            // проверка на < 1 добавлена на случай, если по какой то причине родитель
            // текущей группы пустой - такая группа должна быть удалена
            if (group.parent.children.length <= 1) {
                this.TryUngroup(group.parent);
            }
        }

        if (anchorGroups?.length) {
            for (let i = 0; i < anchorGroups.length; i++) {
                this.deleteProcessor(anchorGroups[i], false, false);
            }
        }
    }

    deleteWidget(widgetUid: string) {
        const group = this.items.find((i) => i.children.some((c) => c.UID === widgetUid));

        if (
            !group ||
            (group.children[0] instanceof CommentWidget && !canv.isBoardModeEditOrOwner())
        )
            return;

        canv.eventManager.openRecord();
        canv.canvasController.deleteProcessor(group);
        canv.eventManager.closeRecord();
    }

    findAllWidgetsByConstructorName(constructorName: string) {
        const result: elementBase[] = [];

        for (const group of this.items) {
            result.push(...group.findAllWidgetsByConstructorName(constructorName));
        }

        return result;
    }

    getEntityByType(entityType: string): Entity | undefined {
        let result: Entity | undefined;

        switch (entityType) {
            case "group":
                result = new Group();
                break;
            case "shapeWidget":
                result = new ShapeWidget();
                break;
            case "stickerWidget":
                result = new stickerWidget();
                break;
            case "sketchWidget":
                result = new SketchWidget();
                break;
            case "textWidget":
                result = new textWidget();
                break;
            case "textWidgetV2":
                result = new TextWidgetV2();
                break;
            case "arrowWidget":
                result = new arrowWidget();
                break;
            case "frameWidget":
                result = new frameWidget(0, 0);
                break;
            case "imageWidget":
                result = new ImageWidget();
                break;
            case "gifWidget":
                result = new GifWidget();
                break;
            case "treeNodeWidget":
                result = new TreeNodeWidget();
                break;
            case "tableWidget":
                result = new TableWidget();
                break;
            case "cardWidget":
                result = new cardWidget();
                break;
            case "mindMapWidget":
                result = new mindMapWidget();
                break;
            case "comment":
                result = new CommentWidget();
                break;
            case "embedWidget":
                result = new EmbedWidget();
                break;
            case "documentWidget":
                result = new DocumentViewerWidget();
                break;
            case "aiAnswer":
                result = new AiAnswer(0, 0);
                break;
            case "emojiWidget":
                result = new EmojiWidget();
                break;
            case "presentationWidget":
                result = new PresentationWidget(0, 0);
                break;
        }

        return result;
    }

    filterCheckedByEntityType(entityType: string) {
        const result: Group[] = [];

        for (let i = 0; i < this.selected.length; i++) {
            if (
                entityType !== "group" &&
                this.selected[i].isItemWrapper() &&
                this.selected[i].onlyChild()?.constructorName === entityType
            ) {
                result.push(this.selected[i]);
            } else if (!this.selected[i].isItemWrapper() && entityType === "group") {
                result.push(this.selected[i]);
            }
        }

        selectionManager.setSelected(result);
    }

    items: Group[] = [];
    groups: Map<string, Group> = new Map();
    pastVotes: VoteEntity[] = [];
    activeVote: VoteEntity | null = null;
    timer: TimerEntity | null = null;
    hashGrid: HashGrid | null = null;

    startX = 0;
    startY = 0;
    width = 0;
    height = 0;

    selected: Group[] = [];
    focused = false;

    fixedWidgets = [];
    copiedGroups = [];

    isCopyProccess = false;

    showComments = true;
    showResolvedComments = false;
    showVotesResults = false;
}
